import { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface I18nSetterProps {
  readonly LocalMFEName: string;
  readonly children: React.ReactNode;
  readonly noSuspense?: boolean;
  readonly suspenseFallbackNode?: React.ReactNode;
  readonly fallbackLanguage: string;
  readonly activeNamespaces: Record<string, string[]>;
}

/**
 * Provider that loads the appropriate translation namespaces for a given MFE.
 * On mount, calls loadNamespaces for the active namespaces.
 * On language change, clears outgoing bundles and reloads.
 */
export function I18nSetter({
  children,
  LocalMFEName,
  noSuspense = false,
  suspenseFallbackNode = <span>Loading</span>,
  fallbackLanguage,
  activeNamespaces
}: Readonly<I18nSetterProps>): React.ReactElement {
  const { i18n: i18nGlobal } = useTranslation();
  const { isInitialized, loadNamespaces } = i18nGlobal;

  useEffect(() => {
    const activeLanguage = i18nGlobal.language;

    if (isInitialized && activeLanguage) {
      const namespaces = activeNamespaces[LocalMFEName];
      if (namespaces?.length) {
        loadNamespaces(namespaces);
      }
    }

    return () => {
      const outgoingLanguage = i18nGlobal.language;
      const outgoingBaseLanguage = outgoingLanguage.split("-")[0];
      if (outgoingLanguage !== i18nGlobal.language) {
        if (
          outgoingBaseLanguage !== outgoingLanguage &&
          outgoingBaseLanguage !== fallbackLanguage
        ) {
          delete i18nGlobal.store.data?.[outgoingBaseLanguage];
        }
        if (outgoingLanguage !== fallbackLanguage) {
          delete i18nGlobal.store.data?.[outgoingLanguage];
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18nGlobal.language, isInitialized, loadNamespaces, LocalMFEName]);

  if (noSuspense) return children as React.ReactElement;

  return <Suspense fallback={suspenseFallbackNode}>{children}</Suspense>;
}
